import { Flex, Box, Heading, Text } from '@chakra-ui/react';
import { useInfiniteQuery } from '@tanstack/react-query';
import NextLink from 'next/link';
import { FC, useMemo, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Virtuoso } from 'react-virtuoso';

import {
  NotificationItem,
  NotificationItemSkeleton,
} from '@/components/ui/shared/notifications/NotificationItem/NotificationItem';
import { ResourceType } from '@/enums/ResourceType';
import { useQueryParams, StringParam } from '@/hooks/useQueryParams';
import { BELL_RESOURCE_TYPES, notificationsInfiniteQueryOptions } from '@/queries/upgrade/notifications';

import { getNotificationResourceLink } from './Notifications.utils';

export interface INotificationsProps {
  pageSize?: number;
  refreshInterval?: number;
  resourceType?: ResourceType | Array<ResourceType>;
}

export const Notifications: FC<INotificationsProps> = ({
  pageSize = 10,
  refreshInterval = 60 * 1000,
  resourceType = BELL_RESOURCE_TYPES,
}) => {
  const { t } = useTranslation();
  const { eventId } = useQueryParams<{ eventId: string }>({
    eventId: StringParam,
  });

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } = useInfiniteQuery({
    ...notificationsInfiniteQueryOptions(eventId, resourceType, pageSize),
    refetchInterval: refreshInterval,
  });

  const items = useMemo(() => data?.pages.flatMap((page) => page.items) ?? [], [data]);
  const isEmpty = !isLoading && items.length === 0;

  const row = useMemo(
    () => (_, item) => {
      return (
        <NextLink href={getNotificationResourceLink(eventId, item)} passHref legacyBehavior>
          <NotificationItem key={item.id} as="a" notification={item} px={{ base: 8, md: 5 }} />
        </NextLink>
      );
    },
    [eventId]
  );

  return (
    <Flex h="full" flexDirection="column">
      <Heading fontSize="2xl" px={{ base: 8, md: 5 }} py={{ base: 6, md: 6 }}>
        {t('notifications.title')}
      </Heading>
      <Box flex={1}>
        {isEmpty ? (
          <Text px={{ base: 8, md: 5 }}>{t('notifications.emptyState')}</Text>
        ) : (
          <Virtuoso
            style={{ paddingBottom: '32px' }}
            data={items}
            endReached={() => hasNextPage && !isFetchingNextPage && fetchNextPage()}
            itemContent={row}
            components={{
              Footer: () => {
                if (isFetchingNextPage) {
                  return (
                    <Fragment>
                      {Array.from({ length: 10 }, (_, i) => (
                        <NotificationItemSkeleton key={i} px={{ base: 8, md: 5 }} />
                      ))}
                    </Fragment>
                  );
                }

                return null;
              },
            }}
          />
        )}
      </Box>
    </Flex>
  );
};
